"use client"

import { useState, useEffect } from "react"
import { getProductsByCategory } from "@/lib/api/dutchie"
import CategoryLayout from "@/components/shop/CategoryLayout"
import type { ProductDisplay } from "@/lib/api/dutchie"

/* ------------------------------------------------------------------ */
/*  Category Configuration                                            */
/* ------------------------------------------------------------------ */

const CATEGORIES = [
  "pre-rolls"
]

const EFFECTS = [
  "Relaxed", "Euphoric", "Happy", "Uplifted", "Creative",
  "Focused", "Energetic", "Sleepy", "Hungry", "Talkative", "Clear Mind"
]

const WEIGHT_ORDER = [
  '.5g', '.6g', '.7g', '1g', '1.4g', '1.5g', '2g', '2.1g', 
  '2.5g', '3g', '3.5g', '4.2g', '5g', '6g'
]

const SUBCATEGORY_REGEX: [RegExp,string][] = [
  [/\b\d+\s*(?:p(?:ac)?k|ct)\b|multi-?pack/i, "Packs"],  // Multi-packs first
  [/infused|kief|hash|rosin|live resin|diamond|distillate|sauce|wax|concentrate|extract|shatter/i, "Infused"], // Infused products
  [/premium|craft|top.?shelf|high.?end/i, "Premium"],     // Premium products
  [/regular|standard|classic/i, "Regular"]                // Explicitly regular products
  // "Singles" will be the default for anything that doesn't match above
]

// Normalize pre-roll weight strings to standard format
const normalizePreRollWeight = (weightStr: string): string => {
  if (!weightStr) return ''
  
  // Clean up the string and extract number
  const cleaned = weightStr.toLowerCase().replace(/[^0-9.]/g, '')
  const num = parseFloat(cleaned)
  
  if (!isNaN(num) && num > 0) {
    // Handle decimal weights like 0.5g, 0.7g
    if (num < 1) {
      return `.${(num * 10).toString().replace('.', '')}g`
    }
    // Handle whole weights like 1g, 2g, etc.
    return `${num}g`
  }
  
  return ''
}

// Extract weights from pre-roll products
const extractPreRollWeights = (p: ProductDisplay, targetWeights: string[]): string[] => {
  const weights = new Set<string>()
  
  // Check main option field
  if (p.option) {
    const normalized = normalizePreRollWeight(p.option)
    if (normalized && targetWeights.includes(normalized)) {
      weights.add(normalized)
    }
  }
  
  // Check variants
  p.variants?.forEach(variant => {
    if (variant.option) {
      const normalized = normalizePreRollWeight(variant.option)
      if (normalized && targetWeights.includes(normalized)) {
        weights.add(normalized)
      }
    }
  })
  
  return Array.from(weights)
}

/* ------------------------------------------------------------------ */
/*  Helper Functions                                                  */
/* ------------------------------------------------------------------ */

const titleCase = (s: string) =>
  s.toLowerCase().split(" ").map(w => w[0].toUpperCase()+w.slice(1)).join(" ")

const categorizePreRollType = (p: ProductDisplay) => {
  // Check subcategory first (most reliable)
  if (p.subcategory === 'INFUSED') {
    return "Infused"
  }
  
  if (!p.name && !p.description) return "Singles"
  
  const text = `${p.name ?? ""} ${p.description ?? ""}`.toLowerCase()
  
  // Check for pack types and infused first
  for (const [regex, category] of SUBCATEGORY_REGEX) {
    if (regex.test(text)) {
      return category
    }
  }
  
  // Check if premium by price (single joint over $30)
  const priceStr = typeof p.price === 'string' ? p.price : String(p.price || '0')
  const price = parseFloat(priceStr.replace(/[^\d.]/g, '') || '0')
  const isPack = /\b\d+\s*(?:p(?:ac)?k|ct)\b|multi-?pack/i.test(text)
  
  if (!isPack && price > 30) {
    return "Premium"
  }
  
  return "Singles"
}

const normalizeWeight = (weight: string): string => {
  if (!weight) return weight
  
  // Remove spaces and normalize
  let normalized = weight.replace(/\s+/g, '').toLowerCase()
  
  // Handle decimal formats
  if (normalized.includes('0.')) {
    normalized = normalized.replace('0.', '.')
  }
  
  // Ensure 'g' suffix
  if (!normalized.endsWith('g')) {
    normalized += 'g'
  }
  
  return normalized
}

const extractWeightFromProduct = (p: ProductDisplay): string => {
  // Try the option field first (most reliable)
  if (p.option) {
    const normalized = normalizeWeight(p.option)
    if (WEIGHT_ORDER.includes(normalized)) {
      return normalized
    }
  }
  
  // Try extracting from name
  const nameMatch = p.name?.match(/(\d+(?:\.\d+)?)\s*g/i)
  if (nameMatch) {
    const normalized = normalizeWeight(nameMatch[1] + 'g')
    if (WEIGHT_ORDER.includes(normalized)) {
      return normalized
    }
  }
  
  return p.option || 'Unknown'
}

const parseEffectsFromText = (p: ProductDisplay): string[] => {
  const text = `${p.name ?? ""} ${p.description ?? ""}`.toLowerCase()
  const foundEffects: string[] = []
  
  EFFECTS.forEach(effect => {
    if (text.includes(effect.toLowerCase())) {
      foundEffects.push(effect)
    }
  })
  
  return foundEffects
}

// Enhanced potency extraction (same as current pre-rolls logic)
const extractPotencyValue = (product: ProductDisplay, type: 'thc' | 'cbd'): number => {
  // First try direct fields
  const directValue = type === 'thc' ? product.thc : product.cbd;
  if (directValue) {
    const match = directValue.match(/(\d+(?:\.\d+)?)/);
    if (match && match[1]) {
      return parseFloat(match[1]);
    }
  }

  // Then try potency objects
  const potencyObj = type === 'thc' ? (product as any).potencyThc : (product as any).potencyCbd;
  if (potencyObj && potencyObj.formatted) {
    const match = potencyObj.formatted.match(/(\d+(?:\.\d+)?)/);
    if (match && match[1]) {
      return parseFloat(match[1]);
    }
  }

  // Finally try to extract from text
  const productText = `${product.name || ''} ${product.description || ''}`.toLowerCase();
  const regex = new RegExp(`${type}:?\\s*(\\d+(?:\\.\\d+)?)\\s*%`, 'i');
  const match = productText.match(regex);

  if (match && match[1]) {
    return parseFloat(match[1]);
  }

  return 0; // Default to 0 if no value found
}

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

export default function PreRollsPage() {
  // Custom fetch function that enhances products
  const fetchProducts = async (): Promise<ProductDisplay[]> => {
    try {
      console.log('Fetching pre-rolls products from categories:', CATEGORIES)
      
      // Get products from API
      const fetchedProducts = await getProductsByCategory("pre-rolls")
      
      console.log(`Pre-rolls products found: ${fetchedProducts.length}`)
      
      // Filter for in-stock products only
      const inStockProducts = fetchedProducts.filter(p => p.inStock)
      
      console.log(`In-stock pre-rolls: ${inStockProducts.length}`)
      
      // Deduplicate by ID
      const uniqueProductsMap = new Map<string, ProductDisplay>()
      inStockProducts.forEach(product => {
        if (!uniqueProductsMap.has(product.id)) {
          uniqueProductsMap.set(product.id, product)
        }
      })
      
      const uniqueProducts = Array.from(uniqueProductsMap.values())
      console.log(`Unique pre-rolls after deduplication: ${uniqueProducts.length}`)
      
      // Enhance products with additional properties
      const enhancedProducts = uniqueProducts.map((product, index) => {
        const thcValue = extractPotencyValue(product, 'thc')
        const cbdValue = extractPotencyValue(product, 'cbd')
        
        // Check if infused
        const isInfused = /infused|kief|hash|rosin|live resin|diamond|distillate|sauce|wax/i.test(
          `${product.name ?? ""} ${product.description ?? ""}`
        )
        
        // Check if pack
        const isPack = /\b\d+\s*(?:p(?:ac)?k|ct)\b|multi-?pack/i.test(
          `${product.name ?? ""}`
        )
        
        // Extract normalized weight
        const normalizedWeight = extractWeightFromProduct(product)
        
        // Parse effects
        const effects = parseEffectsFromText(product)
        
        // Debug log for first few products
        if (index < 3) {
          console.log('Pre-rolls product enhancement:', {
            name: product.name,
            isInfused,
            isPack,
            normalizedWeight,
            effects,
            extractedThc: thcValue,
            extractedCbd: cbdValue
          })
        }
        
        return {
          ...product,
          // Virtual subcategory for filtering
          virtualSubcategory: categorizePreRollType(product),
          // Enhanced properties
          isInfused,
          isPack,
          normalizedWeight,
          effects,
          // Extract THC/CBD numeric values for filtering (use % for pre-rolls)
          thc: thcValue.toString() + '%',
          cbd: cbdValue.toString() + '%'
        }
      })
      
      console.log('fetchProducts returning enhanced pre-rolls:', enhancedProducts.length)
      
      return enhancedProducts
      
    } catch (error) {
      console.error('Error fetching pre-rolls products:', error)
      return []
    }
  }

  const config = {
    title: "Pre-Rolls",
    blurb: "Premium pre-rolled joints for convenient consumption",
    fetch: fetchProducts,

    effects: EFFECTS,
    
    extraFilterChips: (product: ProductDisplay) => {
      // Determine pre-roll type
      let type = "Regular"
      
      // Check subcategory first (most reliable)
      if (product.subcategory === 'INFUSED') {
        type = "Infused"
      } else {
        const text = `${product.name ?? ""} ${product.description ?? ""}`.toLowerCase()
        
        // Check if infused using comprehensive regex
        const isInfused = /infused|kief|hash|rosin|live resin|diamond|distillate|sauce|wax|concentrate|extract|shatter/i.test(text)
        
        if (isInfused) {
          type = "Infused"
        } else {
          // Check if premium (single joint over $30)
          const priceStr = typeof product.price === 'string' ? product.price : String(product.price || '0')
          const price = parseFloat(priceStr.replace(/[^\d.]/g, '') || '0')
          const isPack = /\b\d+\s*(?:p(?:ac)?k|ct)\b|multi-?pack/i.test(text)
          
          // Premium: single joint over $30
          if (!isPack && price > 30) {
            type = "Premium"
          }
        }
      }
      
      return {
        types: type !== "Regular" ? [type] : []
      }
    },
    
    potencyUnit: "%",
    showPotency: true,
    showWeight: true,
    weightRanges: [
      {
        label: "Individual & Small Packs",
        extract: (p: ProductDisplay) => extractPreRollWeights(p, ['.5g', '.6g', '.7g', '1g', '1.4g', '1.5g'])
      },
      {
        label: "Multi-Packs",
        extract: (p: ProductDisplay) => extractPreRollWeights(p, ['2g', '2.1g', '2.5g', '3g', '3.5g', '4.2g', '5g', '6g'])
      }
    ],
    headerTheme: "yellow" as const, // Amber theme
    debug: true
  }

  return (
    <CategoryLayout
      config={config}
      productCardProps={{ 
        buttonClassName: "bg-yellow-600 hover:bg-yellow-700"
      }}
    />
  )
}
